import './KillBar.css'
import { useLayoutEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { STRUCTURES_TO_WIN } from '../../rules/gameConfig'
import { playSound } from '../../utils/soundManager'

// ─── KillBar ────────────────────────────────────────────────────────────────
//
// The scoreboard. One generic structure icon per structure a player can afford
// to lose (STRUCTURES_TO_WIN); `lost` of them are greyed and crossed out, and
// when the whole row is grey that player has lost the war. That is the only
// scoring feedback in the game, so an icon's death is the loudest beat of the
// reveal: it flares white, swells, and settles grey with its X drawn in.
//
// Partial structure damage is shown on the map only — an icon flips exactly
// when a structure's LAST subplot craters. HexBoard lifts the dead
// structure's silhouette off the map and flies it to the next intact icon
// (`data-intact`), ticking `lost` as it lands; the icon then plays its death.
//
// Props:
//   lost    number  — structures this player has lost (0 … total)
//   total   number  — icons in the row (default STRUCTURES_TO_WIN)
//   side    'you' | 'opp'   — You fills from the left edge, Opp from the right
//   silent  bool    — no death sound (demo loops)
//
// The structure glyph: a wide, low industrial block with a stepped annex and
// one tall stack. Drawn as blocks (no fine detail) so it stays a building at
// this size — font icons' rooflines turn to squiggle here. crispEdges keeps
// every corner HARD at tiny scale (antialiasing otherwise rounds them soft).
// Fills with currentColor so the death animation's color ramp drives it; the
// two window slots are dark cut-ins so the silhouette reads as a building,
// not a badge.
function StructureGlyph() {
    return (
        <svg className="kb__glyph" viewBox="0 0 24 20" shapeRendering="crispEdges" aria-hidden="true">
            <g fill="currentColor">
                <rect x="1"  y="9" width="22" height="11" />   {/* main block */}
                <rect x="1"  y="5" width="9"  height="4"  />   {/* stepped annex */}
                <rect x="16" y="0" width="5"  height="9"  />   {/* stack */}
            </g>
            <g className="kb__glyph-cut">
                <rect x="4"  y="12" width="3" height="4" />
                <rect x="10" y="12" width="3" height="4" />
            </g>
        </svg>
    )
}

export default function KillBar({ lost = 0, total = STRUCTURES_TO_WIN, side = 'you', silent = false }) {
    const clamped = Math.max(0, Math.min(total, Math.round(lost ?? 0)))

    // Only icons that die WHILE mounted play the death flash — a count that
    // arrives at mount (reload, history jump) renders settled. Layout effect,
    // not effect: the sound must be scheduled BEFORE the X's first paint
    // (useEffect runs after it, which read as the boom trailing the visual),
    // and the 0.02s offset skips the clip's pre-attack ramp so the audible
    // hit lands with the flash.
    const prev = useRef(clamped)
    const [dying, setDying] = useState(() => new Set())
    useLayoutEffect(() => {
        if (clamped > prev.current) {
            const next = new Set(dying)
            for (let i = prev.current; i < clamped; i++) next.add(i)
            setDying(next)
            if (!silent) playSound('scoreboard', { offset: 0.02 })
        } else if (clamped < prev.current) {
            setDying(new Set())
        }
        prev.current = clamped
    }, [clamped]) // eslint-disable-line react-hooks/exhaustive-deps

    const standing = total - clamped
    return (
        <div
            className={`kb kb--${side}`}
            data-kill-side={side}
            role="img"
            aria-label={`${side === 'you' ? 'Your' : "Opponent's"} structures: ${standing} of ${total} standing`}
        >
            {Array.from({ length: total }, (_, i) => {
                const destroyed = i < clamped
                return (
                    /* The slot is the icon's grid box — a steady grey cell (the
                       setup Grid's ground, echoed) that stays put while the
                       icon inside plays its death swell. A kill INVERTS it:
                       the slot goes black while the corpse takes the grey. */
                    <span key={i} className={`kb__slot${destroyed ? ' kb__slot--destroyed' : ''}`}>
                        <span
                            className={`kb__icon${destroyed ? ' kb__icon--destroyed' : ''}${dying.has(i) ? ' kb__icon--dying' : ''}`}
                            data-kill-icon={`${side}-${i}`}
                            data-intact={destroyed ? 'false' : 'true'}
                            data-testid="kb-icon"
                        >
                            <StructureGlyph />
                            <FontAwesomeIcon icon={faXmark} className="kb__x" aria-hidden="true" />
                        </span>
                    </span>
                )
            })}
        </div>
    )
}
